import { useState } from 'react'
import dayjs from 'dayjs'
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react'
import { initialsOf, JOURNEY_COLOR, MONO, nodeStyle, severityOf, toneOf } from './journeyTheme'
import type { CareJourney, JourneyCell, JourneyVisit } from './journeys'

/**
 * `Jul 28`, or `Aug 1 · 9:14 AM` when the instant carries a real time of day.
 * Date-only values (board due dates) never render a meaningless midnight.
 */
function formatAt(at: string | null): string {
  if (!at) return ''
  const date = dayjs(at)
  if (!date.isValid()) return ''
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(at)
  const midnight = date.hour() === 0 && date.minute() === 0
  return dateOnly || midnight ? date.format('MMM D') : date.format('MMM D · h:mm A')
}

/** Org on its own line above the timestamp, as the design lays it out. */
function detailLines(cell: JourneyCell): string[] {
  return [cell.detail, cell.org, formatAt(cell.at)].filter((part): part is string => Boolean(part))
}

type Stage = { key: string; stage: string; cell: JourneyCell }

/** The six stages of a journey, left to right. Every column is represented. */
function stagesOf(visit: JourneyVisit): Stage[] {
  return [
    { key: 'visit', stage: 'Visit', cell: visit.visit },
    { key: 'request', stage: 'Request', cell: visit.request },
    { key: 'order', stage: 'Order', cell: visit.order },
    { key: 'result', stage: 'Result', cell: visit.result },
    { key: 'notified', stage: 'Notified', cell: visit.notified },
    { key: 'followup', stage: 'Follow-up', cell: visit.nextAction },
  ]
}

const StageNode = ({
  stage,
  previous,
  isFirst,
}: {
  stage: Stage
  previous: Stage | null
  isFirst: boolean
}) => {
  const style = nodeStyle(toneOf(stage.cell), QUIET_COMPLETED)
  // The connector takes the PREVIOUS stage's tone, so the line reads as
  // "progress achieved up to here" rather than colouring the step ahead.
  const link = previous ? nodeStyle(toneOf(previous.cell), QUIET_COMPLETED) : null

  return (
    <div className="relative flex min-w-0 flex-col gap-[11px] pr-3">
      <div className="relative h-3.5">
        {/* Meets both nodes exactly — no overlap, no gap.
            Adjacent nodes sit one grid COLUMN apart, but this line is absolutely
            positioned inside the 14px-tall wrapper, so its `100%` resolves
            against that wrapper — which is 12px narrower than the column
            (the column's `pr-3`). Required length is column − node =
            (100% + 12px) − 14px = 100% − 2px. Dropping the 12px is what left a
            floating line short of the previous node. */}
        {!isFirst && link ? (
          <div
            className="absolute top-[6px] right-full h-0.5 w-[calc(100%-2px)]"
            style={{ borderTop: `2px ${link.lineStyle} ${link.lineColor}` }}
          />
        ) : null}
        <div
          className="absolute top-0 left-0 box-border h-3.5 w-3.5 rounded-full"
          style={{ background: style.fill, border: `2px solid ${style.ring}` }}
        />
      </div>
      <div className="flex min-w-0 flex-col gap-[5px]">
        <div
          className="text-[11px] tracking-[0.11em] uppercase"
          style={{ fontFamily: MONO, color: JOURNEY_COLOR.muted }}
        >
          {stage.stage}
        </div>
        <div
          className="text-[13.5px] leading-[1.35] break-words"
          style={{ color: style.textColor, fontWeight: style.weight }}
        >
          {stage.cell.label}
        </div>
        {detailLines(stage.cell).map((line) => (
          <div
            key={line}
            className="text-[10.5px] leading-[1.55] break-words"
            style={{ fontFamily: MONO, color: JOURNEY_COLOR.muted }}
          >
            {line}
          </div>
        ))}
      </div>
    </div>
  )
}

/** One visit's pipeline plus its own Next / Owner footer. */
const VisitTrack = ({ visit }: { visit: JourneyVisit }) => {
  const stages = stagesOf(visit)
  const nextTone = toneOf(visit.nextAction)

  return (
    <div className="flex flex-col gap-5 px-[30px] py-[26px]">
      <div className="grid grid-cols-2 gap-y-6 sm:grid-cols-3 xl:grid-cols-6">
        {stages.map((stage, index) => (
          <StageNode
            key={stage.key}
            stage={stage}
            previous={index === 0 ? null : stages[index - 1]}
            isFirst={index === 0}
          />
        ))}
      </div>

      <div
        className="flex flex-wrap items-center justify-between gap-5 pt-4"
        style={{ borderTop: `1px solid ${JOURNEY_COLOR.hairline}` }}
      >
        <div className="flex min-w-0 items-baseline gap-2.5">
          <div
            className="shrink-0 text-[11px] tracking-[0.11em] uppercase"
            style={{ fontFamily: MONO, color: JOURNEY_COLOR.muted }}
          >
            Next
          </div>
          {/* Takes the stage's own tone rather than assuming there is something
              to act on. A stage that is merely unreachable — "Pending result",
              while the result is still out — is grey above, and printing it in
              action-blue down here contradicted that. */}
          <div
            className="text-[13.5px] leading-[1.4]"
            style={{ color: nodeStyle(nextTone, QUIET_COMPLETED).textColor }}
          >
            {visit.nextAction.detail ?? visit.nextAction.label}
          </div>
        </div>
        {/* Labelled for the same reason "Next" is: a bare name in the corner of
            a card full of names (patient, practitioner) does not say which role
            it plays. Same uppercase-mono treatment as "Next" so the footer reads
            as one pair of labelled fields rather than a label and a loose name. */}
        <div className="flex min-w-0 items-baseline gap-2.5">
          <div
            className="shrink-0 text-[11px] tracking-[0.11em] uppercase"
            style={{ fontFamily: MONO, color: JOURNEY_COLOR.muted }}
          >
            Owner
          </div>
          <div className="text-[12.5px] whitespace-nowrap" style={{ color: JOURNEY_COLOR.subtle }}>
            {visit.owner?.display ?? 'Unassigned'}
          </div>
        </div>
      </div>
    </div>
  )
}

// Completed steps read as solid, filled nodes rather than hollow outlines, so a
// finished stage looks finished at a glance and the track shows how far a
// journey has actually got. Exceptions still stand out by colour, not by being
// the only thing filled in.
const QUIET_COMPLETED = false

export const JourneyCard = ({ journey }: { journey: CareJourney }) => {
  const [expanded, setExpanded] = useState(false)
  const [latest, ...rest] = journey.visits
  // The chip describes the pipeline shown beside it — the LATEST visit — so the
  // two numbers on screen can never contradict each other. The patient-level
  // worst still surfaces, on the control that reveals where it lives.
  const severity = severityOf(latest?.overdueDays ?? null)
  const worstHidden =
    journey.overdueDays !== null && journey.overdueDays > (latest?.overdueDays ?? 0)
      ? journey.overdueDays
      : null
  const expandable = rest.length > 0
  if (!latest) return null

  return (
    <div
      className="overflow-hidden rounded-[14px] bg-white"
      style={{
        border: `1px solid ${JOURNEY_COLOR.border}`,
        boxShadow: '0 1px 2px rgba(27,36,48,0.04)',
      }}
    >
      <div className="grid grid-cols-1 lg:grid-cols-[264px_1fr]">
        <div
          className="flex items-start gap-3.5 py-6 pr-6 pl-[22px]"
          style={{
            borderLeft: `3px solid ${severity.bar}`,
            borderRight: `1px solid ${JOURNEY_COLOR.hairline}`,
          }}
        >
          <div
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[12.5px] font-semibold"
            style={{ background: '#edf1f6', color: '#59657a' }}
          >
            {initialsOf(journey.patientName)}
          </div>
          <div className="flex min-w-0 flex-col gap-1.5">
            <div
              className="text-[16px] font-semibold tracking-[-0.01em]"
              style={{ color: JOURNEY_COLOR.ink }}
            >
              {journey.patientName}
            </div>
            <div className="text-[12.5px] leading-[1.4]" style={{ color: '#6b7688' }}>
              {latest.practitioner?.display ?? 'No practitioner recorded'}
            </div>
            <div
              className="text-[11px] leading-[1.5]"
              style={{ fontFamily: MONO, color: JOURNEY_COLOR.muted }}
            >
              {journey.visitCount} {journey.visitCount === 1 ? 'visit' : 'visits'} · last{' '}
              {formatAt(latest.visitDate)}
            </div>
            <div
              className="mt-1 self-start rounded-[5px] px-[9px] py-1 text-[10.5px] tracking-[0.05em]"
              style={{ fontFamily: MONO, background: severity.bg, color: severity.color }}
            >
              {severity.label}
            </div>
            {expandable ? (
              <button
                type="button"
                onClick={() => setExpanded((open) => !open)}
                aria-expanded={expanded}
                className="mt-1 flex items-start gap-1 self-start text-left text-[12px] hover:underline"
                style={{ color: JOURNEY_COLOR.wait }}
              >
                <span className="mt-[2px] shrink-0">
                  {expanded ? <IconChevronDown size={13} /> : <IconChevronRight size={13} />}
                </span>
                <span>
                  {expanded ? 'Hide' : 'Show'} {rest.length} earlier{' '}
                  {rest.length === 1 ? 'visit' : 'visits'}
                  {/* Named here rather than in the chip, so the worst figure sits
                      next to the control that reveals the visit it came from. */}
                  {worstHidden !== null ? (
                    <span style={{ color: JOURNEY_COLOR.late }}>
                      {' '}
                      · up to {worstHidden}d overdue
                    </span>
                  ) : null}
                </span>
              </button>
            ) : null}
          </div>
        </div>

        <div className="min-w-0">
          <VisitTrack visit={latest} />
        </div>
      </div>

      {/* Earlier visits keep their own complete pipeline — a later visit must
          never appear to have completed an older visit's outstanding action. */}
      {expanded
        ? rest.map((visit, index) => (
            <div
              key={visit.encounterId ?? visit.appointmentId ?? index}
              className="grid grid-cols-1 lg:grid-cols-[264px_1fr]"
              style={{ borderTop: `1px solid ${JOURNEY_COLOR.hairline}`, background: '#fcfdfe' }}
            >
              <div
                className="flex flex-col gap-1 py-6 pr-6 pl-[22px]"
                style={{
                  borderLeft: `3px solid ${JOURNEY_COLOR.ringIdle}`,
                  borderRight: `1px solid ${JOURNEY_COLOR.hairline}`,
                }}
              >
                <div
                  className="text-[11px] tracking-[0.11em] uppercase"
                  style={{ fontFamily: MONO, color: JOURNEY_COLOR.muted }}
                >
                  Earlier visit
                </div>
                <div
                  className="text-[12px] leading-[1.5]"
                  style={{ fontFamily: MONO, color: JOURNEY_COLOR.muted }}
                >
                  {formatAt(visit.visitDate)}
                </div>
                <div className="text-[12.5px] leading-[1.4]" style={{ color: '#6b7688' }}>
                  {visit.practitioner?.display ?? '—'}
                </div>
              </div>
              <div className="min-w-0">
                <VisitTrack visit={visit} />
              </div>
            </div>
          ))
        : null}
    </div>
  )
}
